import React, { useEffect, useRef, useState } from "react";
import styled, { css } from 'styled-components';
import colors from "../../../utils/colors";
import Block from '../../Block';
import Text from '../../Text';

function DropDownInput(
    {
        label,
        description,
        error,
        disabled,
        placeholder,
        onChangeValue,
        options = [],
        value
    }
) {
    const ref = useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        }
    }, []);

    const handleClickOutside = (event) => {
        if (ref && !ref.current.contains(event.target)) {
            setIsOpen(false);
        }
    }

    const onClick = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    }

    return (
        <Block ref={ref} width="100%">
            {!!label && <Text size={12} height={16} mb={8} color={colors.grey03}>{label}</Text>}
            <Block style={{ position: "relative" }}>
                {(!error) && <AngleWrapper disabled={disabled} onClick={onClick}>{isOpen ? <span className="icon-Angle_up" /> : <span className="icon-Angle_down" />}</AngleWrapper>}
                {(!!error) && <StatusWrapper><span className="icon-Error" /></StatusWrapper>}
                <Input
                    error={error}
                    disabled={disabled}
                    onClick={onClick}
                >
                    {(!value && !!placeholder) && <Text size={14} height={24} color={colors.grey50}>{placeholder}</Text>}
                    {(!!value) && <Text size={14} height={24} color={disabled ? colors.grey20 : colors.grey100}>{value.label}</Text>}
                </Input>
                {isOpen && <Options>
                    {options.map((option, index) => {
                        const selected = !!value && value.value === option.value;
                        return (
                            <Option key={index} onClick={() => { onChangeValue(option); setIsOpen(false); }}>
                                <Text size={14} height={24} color={selected ? colors.grey100 : colors.grey50}>{option.label}</Text>
                                {(selected) && <Checked className="icon-Check" />}
                            </Option>
                        );
                    })}
                </Options>}
            </Block>
            {(() => {
                let color = null;
                let text = null;
                if (!!error) { text = typeof error === "string" ? error : null; color = colors.red01; }
                else if (!!description) { text = description; color = colors.grey50; }

                if (!!text) {
                    return (
                        <Block mt={8}>
                            <Text
                                size={11}
                                height={16}
                                color={color}
                            >
                                {error || description}
                            </Text>
                        </Block>
                    );
                }
            })()}
        </Block>
    );
}

export default React.memo(DropDownInput);

const Input = styled.div`
    background-color: ${colors.white01};
    height: 48px;
    color: ${colors.grey100};
    font-size: 14px;
    line-height: 24px;
    font-family: "MedistrapSemiBold";
    border: 0;
    padding: 12px 40px 12px 16px;
    border: 1px solid ${colors.grey20};
    border-radius: 10px;
    user-select: none;
    
    ${({ disabled }) => !disabled && css`
        cursor: pointer;
    `}

    :hover {
        border-color: ${({ error }) => {
        if (!!error) {
            return colors.alert100;
        }

        return colors.grey60;
    }};
    }

    ${({ disabled }) => !!disabled && css`
        color: ${colors.grey20};
        background-color: ${colors.grey5};
        border-color: ${colors.grey20};
    `}

    ${({ error }) => !!error && css`
        border-color:  ${colors.alert100};
    `}

    overflow: hidden;

    span {
        display: inline-block; 
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        user-select: none;
        width: calc(100%);
    }
`;

const AngleWrapper = styled(Block)`
    position: absolute;
    top: 16px;
    right: 16px;


    ${({ disabled }) => !disabled && css`
        cursor: pointer;
    `}
`;

const StatusWrapper = styled(Block)`
    position: absolute;
    top: 16px;
    right: 16px;
`;

const Options = styled.div`
    position: absolute;
    top: 56px;
    left: 0px;
    right: 0px;
    max-height: 128px;
    background-color: ${colors.white01};
    border: 1px solid ${colors.brand100};
    border-radius: 10px;
    z-index: 2;
    overflow-y: auto;

    span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        user-select: none;
    }
`;

const Checked = styled.span`
    position: absolute;
    top: 12px;
    right: 12px;
    ::before {
        color: ${colors.brand100};
    }
`;

const Option = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    height: 40px;
    cursor: pointer;
    width: 100%;
    padding: 0 40px 0 16px;

    :hover {
        background-color: ${colors.brand100};

        span {
            color: ${colors.white100} !important;
        }

        ${Checked}::before {
            color: ${colors.white100};
        }
    }
`;
